import { exists } from ".";

export class AggregateError extends Error {
  constructor(
    message: string,
    public readonly errors: readonly any[],
  ) {
    super(message);
  }
}

type ResultOrError<TResult> = {
  result?: TResult;
  error?: any;
};

/**
 * Handle promises in batches of `n`. Adding more promises will not do them
 * until the batch is done. Will hold on to results in case one throws.
 * Different options for handling those situations.
 *
 * @deprecated Use `PromiseQueue`. It's not an exact 1:1 functionally, but this
 * class has some quirks that aren't desirable. */
export class PromiseBatcher<TResult = void> {
  private isFinalized = false;
  private promises: Array<Promise<ResultOrError<TResult>>> = [];
  private results: Array<ResultOrError<TResult>> = [];

  constructor(
    readonly batchSize = 100,
    private readonly options?: {
      discardResults?: boolean;
      throwErrorsOn?: "batch" | "finish";
      onError?: (error: any) => void;
    },
  ) {
    if (options?.discardResults && options.throwErrorsOn === "finish") {
      throw new Error(
        "throwErrorsOn=finish and discardResults=true can't be used together",
      );
    }
  }

  async add(promise: Promise<TResult> | (() => Promise<TResult>)) {
    if (this.isFinalized) {
      throw new Error("Promise added to batcher after finish(true) called.");
    }

    if (typeof promise === "function") {
      promise = promise();
    }

    this.promises.push(
      promise
        .then((result) => ({ result }))
        .catch((error) => {
          this.options?.onError?.(error);
          return { error };
        }),
    );
    if (this.promises.length >= this.batchSize) {
      return await this.doBatch(false);
    }
  }

  async finish(finalize?: true) {
    await this.doBatch(true);
    this.isFinalized = finalize ?? false;
    if (this.options?.discardResults) return undefined;
    return this.results;
  }

  private checkForErrors(
    results: Array<ResultOrError<unknown>>,
    throwOnErrors: boolean,
  ) {
    if (!throwOnErrors) return;

    const errorResults = results.map((r) => r.error).filter(exists);

    if (errorResults.length === 0) return;
    throw new AggregateError(
      "One or more batch operations failed.",
      errorResults,
    );
  }

  private async doBatch(isFinish: boolean) {
    const batchResults: ResultOrError<TResult>[] = [];
    do {
      const [...batchPromises] = this.promises;
      this.promises = [];
      batchResults.push(...(await Promise.all(batchPromises)));
      //
      // handle re-entrancy - if we're finishing the batcher, keep checking
      // until we have not accumulated any more promises
      //
    } while (isFinish && this.promises.length > 0);

    this.checkForErrors(batchResults, this.options?.throwErrorsOn === "batch");

    if (!this.options?.discardResults) {
      this.results.push(...batchResults);
      this.checkForErrors(
        this.results,
        this.options?.throwErrorsOn === "finish" && isFinish,
      );
    }

    if (batchResults.length === 0) return undefined;

    return batchResults;
  }
}
